'use client'

import Link from 'next/link'
import { LEAGUE_NAME } from '@/lib/config/brand'
import { useEffect, useMemo, useState } from 'react'

type HomeNewsItem = {
  slug: string
  tag: string
  title: string
  headline: string
}

const PAGE_SIZE = 12

export function HomeNewsSection({ items }: { items: HomeNewsItem[] }) {
  const [query, setQuery] = useState('')
  const [page, setPage] = useState(1)

  const filteredItems = useMemo(() => {
    const normalized = query.trim().toLowerCase()
    if (!normalized) return items
    return items.filter((item) =>
      [item.tag, item.title, item.headline].some((value) => value.toLowerCase().includes(normalized))
    )
  }, [items, query])

  const totalPages = Math.max(1, Math.ceil(filteredItems.length / PAGE_SIZE))

  useEffect(() => {
    setPage(1)
  }, [query])

  useEffect(() => {
    if (page > totalPages) setPage(totalPages)
  }, [page, totalPages])

  const pageItems = filteredItems.slice((page - 1) * PAGE_SIZE, page * PAGE_SIZE)

  return (
    <section className="shell pb-14">
      <div className="flex flex-wrap items-end justify-between gap-4">
        <div>
          <p className="league-kicker">News</p>
          <h2 className="mt-2 font-display text-[32px] font-bold leading-none tracking-[-0.03em] text-[color:var(--league-ink)] md:text-[36px]">
            Ultime dal movimento
          </h2>
        </div>
        <div className="flex flex-wrap items-center gap-3">
          <label className="flex min-h-11 items-center rounded-full border border-[color:var(--league-line)] bg-[color:var(--league-surface)] px-3 py-2 text-[14px] text-[color:var(--league-muted)] shadow-sm">
            <svg className="mr-2 h-4 w-4" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
              <path strokeLinecap="round" strokeLinejoin="round" d="m21 21-4.35-4.35" />
              <circle cx="11" cy="11" r="6" />
            </svg>
            <input
              value={query}
              onChange={(event) => setQuery(event.target.value)}
              placeholder="Cerca news"
              aria-label="Cerca news"
              className="min-h-11 w-[180px] bg-transparent text-[16px] outline-none placeholder:text-[color:var(--league-muted)] md:w-[220px]"
            />
          </label>
          <span className="league-badge bg-[color:var(--league-surface-alt)] text-[color:var(--league-muted)]">
            {filteredItems.length} risultati
          </span>
        </div>
      </div>

      <div className="mt-6 grid gap-4 lg:grid-cols-3">
        {pageItems.length === 0 ? (
          <div className="league-card rounded-2xl px-5 py-8 text-center text-[16px] text-[color:var(--league-muted)] lg:col-span-3">
            Nessuna comunicazione pubblicata al momento.
          </div>
        ) : pageItems.map((item, index) => (
          <Link
            href={`/news/${encodeURIComponent(item.slug)}`}
            key={item.slug}
            className="league-card group flex min-h-[104px] items-stretch justify-between gap-3 overflow-hidden rounded-2xl p-0 transition-all hover:border-[color:var(--league-brand-vivid)] hover:shadow-[var(--league-shadow-md)]"
          >
            <div
              className="league-news-tint relative aspect-square self-stretch overflow-hidden bg-[color:var(--league-surface-alt)]"
              style={{ ['--tint' as string]: `linear-gradient(145deg, rgba(244,180,0,${0.28 + (index % 3) * 0.06}) 0%, rgba(12,31,77,${0.72 + (index % 4) * 0.04}) 100%)` }}
            >
              <div className="absolute inset-0 z-20 flex items-center justify-center p-3">
                <div className="relative h-[62%] w-[62%]">
                  <img
                    src="/api/logo?src=%2Flegapuglia.png&threshold=20"
                    alt={LEAGUE_NAME}
                    loading="lazy"
                    decoding="async"
                    className="h-full w-full object-contain"
                  />
                </div>
              </div>
            </div>

            <div className="flex min-w-0 flex-1 flex-col justify-center pr-3 py-3">
              <span className="league-badge w-fit px-2 py-0.5 text-[12px] font-semibold uppercase tracking-[0.12em] text-[color:var(--league-brand-vivid)]">
                {item.tag}
              </span>
              <h3 className="mt-1 line-clamp-1 font-display text-[20px] font-bold leading-none tracking-[-0.03em] text-[color:var(--league-ink)] transition-colors group-hover:text-[color:var(--league-brand-vivid)]">
                {item.title}
              </h3>
              <p className="mt-1 line-clamp-2 text-[14px] leading-5 text-[color:var(--league-muted)]">
                {item.headline}
              </p>
            </div>
          </Link>
        ))}
      </div>

      <div className="mt-6 flex flex-wrap items-center justify-between gap-3">
        <p className="text-[14px] text-[color:var(--league-muted)]">
          Pagina {page} di {totalPages}
        </p>
        <div className="flex items-center gap-2">
          <button
            type="button"
            onClick={() => setPage((current) => Math.max(1, current - 1))}
            disabled={page === 1}
            className="min-h-11 rounded-full border border-[color:var(--league-line)] px-3 py-1.5 text-[14px] font-medium text-[color:var(--league-ink)] transition-opacity disabled:cursor-not-allowed disabled:opacity-40"
          >
            Indietro
          </button>
          {Array.from({ length: totalPages }, (_, idx) => idx + 1).map((pageNumber) => (
            <button
              key={pageNumber}
              type="button"
              onClick={() => setPage(pageNumber)}
              className={`min-h-11 min-w-11 rounded-full px-3 text-[14px] font-semibold transition-colors ${pageNumber === page
                ? 'bg-[color:var(--league-brand-solid)] text-white'
                : 'border border-[color:var(--league-line)] text-[color:var(--league-ink)]'
                }`}
            >
              {pageNumber}
            </button>
          ))}
          <button
            type="button"
            onClick={() => setPage((current) => Math.min(totalPages, current + 1))}
            disabled={page === totalPages}
            className="min-h-11 rounded-full border border-[color:var(--league-line)] px-3 py-1.5 text-[14px] font-medium text-[color:var(--league-ink)] transition-opacity disabled:cursor-not-allowed disabled:opacity-40"
          >
            Avanti
          </button>
        </div>
      </div>
    </section>
  )
}
